/**
 * Filtering Utilities
 * 
 * Pure functions for filtering projects based on search, client, and lead artist criteria.
 * Isolated for easy testing and reuse.
 */

import { ProjectWithRelations } from "@shared/schema";

export interface FilterCriteria {
  searchFilter: string;
  clientFilter: string;
  leadArtistFilter: string;
  pinnedProjectsEnabled: boolean;
  pinnedProjects: Array<{projectId: string, title: string}>;
  hideFinals: boolean;
  hideCanceled: boolean;
  hideWithEfforts: boolean;
}

/**
 * Filter projects based on multiple criteria
 */
export function filterProjects(
  projects: ProjectWithRelations[],
  criteria: FilterCriteria
): ProjectWithRelations[] {
  return projects.filter(project => {
    // Pinned-projects filter. When the "show only pinned" toggle is on we
    // treat pinned membership as the SOLE gate: a pinned project bypasses
    // every other filter (hideFinals, search text, client, lead artist).
    // The pin is the user's explicit "I want to see this regardless of
    // the active filter context" intent — e.g. pinning a project from the
    // cmd+k palette while filtering by another artist must not hide the
    // pinned project. Non-pinned projects are dropped immediately when
    // the toggle is on; no further criteria are evaluated.
    if (criteria.pinnedProjectsEnabled) {
      const isPinned = criteria.pinnedProjects.some(p => p.projectId === project.id);
      return isPinned;
    }

    const status = project.projectStatus?.toLowerCase() ?? '';
    if (criteria.hideFinals && status === 'final') {
      return false;
    }
    // Canceled is the blank status: "empty" is the configured key, while
    // "none" and absent values are also rendered blank by StatusBadge.
    if (criteria.hideCanceled && ['empty', 'none', ''].includes(status)) {
      return false;
    }

    // "Hide with Efforts" filter — skip any project that has at least one
    // effort pulse (category 'effort'). Effort pulses are how production
    // effort/hours are tracked; enabling this hides every project where any
    // effort estimation has been started.
    if (criteria.hideWithEfforts) {
      const hasEffortPulse = project.pulses?.some(p => p.category === 'effort');
      if (hasEffortPulse) {
        return false;
      }
    }
    
    // Search filter - check project ID and name
    const searchMatch = !criteria.searchFilter || 
      project.id.toLowerCase().includes(criteria.searchFilter.toLowerCase()) ||
      project.projectName.toLowerCase().includes(criteria.searchFilter.toLowerCase());
    
    // Client filter
    const clientMatch = !criteria.clientFilter || project.clientId === criteria.clientFilter;
    
    // Artist filter - match if artist is either lead artist OR in additional artists
    const artistMatch = !criteria.leadArtistFilter || 
      project.leadArtist === criteria.leadArtistFilter ||
      (project.artists && project.artists.some(artist => artist.artistCode === criteria.leadArtistFilter));
    
    return searchMatch && clientMatch && artistMatch;
  });
}

/**
 * Check if any filters are currently active
 */
export function hasActiveFilters(criteria: FilterCriteria): boolean {
  return !!(
    criteria.searchFilter ||
    criteria.clientFilter ||
    criteria.leadArtistFilter ||
    criteria.pinnedProjectsEnabled ||
    criteria.hideFinals ||
    // Hidden canceled projects are the default; showing them is the filter.
    !criteria.hideCanceled ||
    criteria.hideWithEfforts
  );
}
